import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Stack,
    Typography,
    Paper,
    IconButton,
    Dialog,
    DialogContent,
    Toolbar,
    Button
} from "@mui/material";
import { useFormikContext } from "formik";
import Upload from "../Images/Upload.png";
import CloseIcon from "@mui/icons-material/Close";
import ArrowRightTwoToneIcon from '@mui/icons-material/ArrowRightTwoTone';
import ArrowLeftTwoToneIcon from '@mui/icons-material/ArrowLeftTwoTone';
import { getFiles, deleteFile, downloadFile, fileUpload } from "../api";
import PayrollsPopup from "./PayrollPopup";
import { useAlerts } from "../Hooks";
import AttachmentTable from "./AttachmentTable"; 
import { useSnackbar } from "notistack";

const toBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = () => {
            const base64 = btoa(
                new Uint8Array(reader.result).reduce(
                    (data, byte) => data + String.fromCharCode(byte),
                    ""
                )
            );
            resolve(base64);
        };
        reader.onerror = reject;
    });

const SharedAttachments = ({ 
    token, 
    showPreview = true, 
    documentType = "Document", 
    attachmentsField = "attachments",
    refIdOverride,
    corpIdOverride,
    directUpload = false,
    allowedExtensions = [".jpeg", ".jpg", ".gif", ".png", ".xls", ".xlsx", ".doc", ".docx", ".pdf", ".txt", ".xps"]
}) => {
    const { values, setFieldValue } = useFormikContext();
    const files = values[attachmentsField] || [];
    const [existingFiles, setExistingFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewFileName, setPreviewFileName] = useState("");
    const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
    const [previewableFiles, setPreviewableFiles] = useState([]);
    const fileInputRef = useRef(null);
    const [, setLoading] = useState(false);
    const [, setActionLoading] = useState(false);

    // Uses overrides if provided, otherwise falls back to standard formik values
    const { enqueueSnackbar } = useSnackbar();
    const currentRefId = refIdOverride || values.id;
    const currentCorpId = corpIdOverride || values.corporation;

    const { popup: alertPopup, showDeleteConfirm, hidePopup } = useAlerts();

    useEffect(() => {
        const fetchFiles = async () => {
            if (!currentRefId) return;
            setLoading(true);
            try {
                const response = await getFiles(currentRefId, token);
                if ((response?.StatusCode === 200 || response?.Status === "File found") && response.Files) {
                    setExistingFiles(response.Files.map(f => ({
                        id: f.AwsPath,
                        name: f.Filename,
                        url: f.AwsPath,
                        isExisting: true
                    })));
                }
            } catch (error) {
                console.error("Failed to fetch files", error);
            } finally {
                setLoading(false);
            }
        };
        fetchFiles();
    }, [currentRefId, token]);

    useEffect(() => {
        const currentFiles = values[attachmentsField] || [];
        const allFiles = [...existingFiles, ...currentFiles];
        const filtered = allFiles.filter(f => {
            const name = (f.name || "").toLowerCase();
        return !name.endsWith(".xls") && !name.endsWith(".xlsx") && !name.endsWith(".csv") && !name.endsWith(".doc") && !name.endsWith(".docx") && !name.endsWith(".txt");
        });
        setPreviewableFiles(filtered);
    }, [existingFiles, values[attachmentsField]]);

    const processFiles = async (fileList) => {
        const allCurrentNames = new Set([
            ...existingFiles.map((f) => f.name?.toLowerCase()),
            ...files.map((f) => f.name?.toLowerCase())
        ]);

        const uniqueFiles = [];
        const duplicateNames = [];
        const invalidFiles = [];
        const specialCharFiles = [];

        fileList.forEach((file) => {
            const fileName = file.name?.toLowerCase() || "";
            const isValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext.toLowerCase()));
            const hasSpecialChars = /[^a-zA-Z0-9.\-_ ()]/g.test(file.name);

            if (hasSpecialChars) {
                specialCharFiles.push(file.name);
            } else if (!isValidExtension) {
                invalidFiles.push(file.name);
            } else if (allCurrentNames.has(fileName)) {
                duplicateNames.push(file.name);
            } else {
                uniqueFiles.push(file);
                allCurrentNames.add(fileName);
            }
        });

        let filesToProcess = uniqueFiles;
        let errorMessage = "";

        if (specialCharFiles.length > 0) {
            errorMessage += `File name cannot contain special characters: ${specialCharFiles.join(", ")}. `;
        }

        if (invalidFiles.length > 0) {
            errorMessage += `Invalid file format(s): ${invalidFiles.join(", ")}. Allowed types: ${allowedExtensions.map(e => e.replace(".", "")).join(", ")}. `;
        }

        if (duplicateNames.length > 0) {
            errorMessage += `File(s) already exist: ${duplicateNames.join(", ")}. `;
        }

        const currentTotal = existingFiles.length + files.length;
        if (currentTotal + filesToProcess.length > 5) {
            errorMessage += "You can only attach a maximum of 5 files.";
            const allowedCount = 5 - currentTotal;
            filesToProcess = allowedCount > 0 ? filesToProcess.slice(0, allowedCount) : [];
        }

        if (errorMessage) {
            enqueueSnackbar(errorMessage.trim(), { variant: "error" }); 
        }

        if (filesToProcess.length === 0) return;

        setActionLoading(true);
        try {
            const processedFiles = await Promise.all(
                filesToProcess.map(async (file) => ({
                    id: Date.now() + Math.random(),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    base64: await toBase64(file),
                }))
            );

            if (directUpload && currentRefId) {
                const payload = {
                    CorporationID: currentCorpId,
                    RefID: currentRefId,
                    Type: documentType,
                    Files: processedFiles.map(f => ({
                        FileName: f.name,
                        FileContent: f.base64,
                        Length: f.size,
                        DocumentType: f.type
                    }))
                };
                const response = await fileUpload(payload, token);
                if (response?.StatusCode === 200 || response?.Status === "Success") {
                    enqueueSnackbar("File(s) uploaded successfully.", { variant: "success" });
                    const refreshRes = await getFiles(currentRefId, token);
                    if ((refreshRes?.StatusCode === 200 || refreshRes?.Status === "File found") && refreshRes.Files) {
                        setExistingFiles(refreshRes.Files.map(f => ({ id: f.AwsPath, name: f.Filename, url: f.AwsPath, isExisting: true })));
                    }
                } else {
                    enqueueSnackbar(response?.Message || "Failed to upload file(s).", { variant: "error" });
                }
            } else {
                setFieldValue(attachmentsField, [...files, ...processedFiles]);
            }
        } catch (error) {
            enqueueSnackbar("An error occurred while processing files.", { variant: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) {
            processFiles(Array.from(e.dataTransfer.files));
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files.length > 0) {
            processFiles(Array.from(e.target.files));
        }
        e.target.value = null;
    };

    const handleDelete = (id, isExisting) => {
        showDeleteConfirm("Are you sure you want to delete?", () => {
            performDelete(id, isExisting);
        });
    };

    const performDelete = async (id, isExisting) => {
        if (isExisting) {
            const file = existingFiles.find(f => f.id === id);
            if (!file) return;

            setActionLoading(true);
                     
            const payload = {
                CorporationID: currentCorpId,
                RefID: currentRefId,
                // Type: documentType,
                Files: [
                    {
                        FileName: file.name,
                        FileContent: "",
                        Length: 0,
                        DocumentType: file.type
                    }
                ]
            };

            try {
                const response = await deleteFile(payload, token);
                if (response?.StatusCode === 200 || response?.Status === "Success") {
                    setExistingFiles(prev => prev.filter(f => f.id !== id));
                    enqueueSnackbar("File deleted successfully.", { variant: "success" });
                } else {
                    enqueueSnackbar(response?.Message || "Failed to delete file", { variant: "error" });
                }
            } catch (error) {
                enqueueSnackbar("An error occurred while deleting the file.", { variant: "error" });
            } finally {
                setActionLoading(false);
            }
        } else {
            setFieldValue(attachmentsField, files.filter((f) => f.id !== id));
            enqueueSnackbar("File deleted successfully.", { variant: "success" });
        }
    };

    const setPreview = (file) => {
        if (!file) {
            setPreviewOpen(false);
            return;
        }
        
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }

        if (file.url) {
            let finalUrl = file.url;
            if (finalUrl.toLowerCase().includes(".pdf") && !finalUrl.includes("#")) {
                finalUrl += "#toolbar=1";
            }
            setPreviewUrl(finalUrl);
            setPreviewFileName(file.name);
        } else if (file.base64) {
            const mimeType = file.type || "application/octet-stream";
            
            const byteCharacters = atob(file.base64);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }
            const fileObj = new File([byteArray], file.name || "Preview.pdf", { type: mimeType });
            let url = URL.createObjectURL(fileObj);

            if (mimeType === "application/pdf" || (file.name && file.name.toLowerCase().endsWith(".pdf"))) {
                url += "#toolbar=1";
            }
            setPreviewUrl(url);
            setPreviewFileName(file.name);
        }
        setPreviewOpen(true);
    };

    const handlePreview = (file) => {
        const index = previewableFiles.findIndex(f => f.id === file.id);
        if (index > -1) {
            setCurrentPreviewIndex(index);
            setPreview(previewableFiles[index]);
        } else {
            setPreview(file);
        }
    };

    const handleNextPreview = () => {
        const nextIndex = currentPreviewIndex + 1;
        if (nextIndex < previewableFiles.length) {
            setCurrentPreviewIndex(nextIndex);
            setPreview(previewableFiles[nextIndex]);
        }
    };

    const handlePreviousPreview = () => {
        const prevIndex = currentPreviewIndex - 1;
        if (prevIndex >= 0) {
            setCurrentPreviewIndex(prevIndex);
            setPreview(previewableFiles[prevIndex]);
        }
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        if (previewUrl && previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setPreviewFileName("");
    };

    const handleDownload = async (file) => {
        if (!file.isExisting) return;

        const payload = {
            ReferenceID: currentRefId,
            FileName: file.name
        };

        setActionLoading(true);
        try {
            const response = await downloadFile(payload, token);
            
            if (response?.StatusCode === 200 && response?.File?.Files?.[0]?.FileContent) {
                const fileData = response.File.Files[0];
                const base64Content = fileData.FileContent;
                
                const byteCharacters = atob(base64Content);
                const byteArray = new Uint8Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteArray[i] = byteCharacters.charCodeAt(i);
                }
                const blob = new Blob([byteArray], { type: "application/octet-stream" });
                
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileData.FileName || file.name);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else { 
                enqueueSnackbar("Download failed or file content missing.", { variant: "error" }); 
            }
        } catch (error) {
            enqueueSnackbar("Download failed.", { variant: "error" }); 
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <Stack spacing={1} sx={{ p: 0 }}>
            <Box>
                <Paper
                    variant="outlined"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current.click()}
                    sx={{
                        mt: 0,
                        p: 1,
                        borderStyle: "dashed",
                        border: isDragging ? "2px dashed #4f46e5" : "1px dashed ",
                        bgcolor: isDragging ? "#f0f7ff" : "transparent",
                        textAlign: "center",
                        color: "#444",
                        cursor: "pointer"
                    }}
                >
                    <input type="file" accept={allowedExtensions.join(",")} multiple hidden ref={fileInputRef} onChange={handleFileSelect} />
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                        <img src={Upload} alt="upload" width={35} height={35} />
                        <Box sx={{ textAlign: "left" }}>
                            <Typography sx={{ fontWeight: 600, fontSize: "20px" }}>Drag and drop or upload files</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>Allowed file types : {allowedExtensions.map(ext => ext.replace(".", "")).join(", ")}</Typography>
                        </Box>
                    </Stack>
                </Paper>

                <AttachmentTable files={[...existingFiles, ...files]} showPreview={showPreview} onPreview={handlePreview} onDownload={handleDownload} onDelete={handleDelete} />
            </Box>

            <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth={false} sx={{ "& .MuiDialog-paper": { width: "75%", height: "85%", maxWidth: "none", maxHeight: "none", display: "flex", flexDirection: "column" } }}>
                <Toolbar sx={{ backgroundColor: "#F8F9FA", minHeight: '37px !important', gap: 1 }}>
                    <Typography sx={{ flex: 1, }} component="div" fontWeight={600} fontSize={"14px"}>{previewFileName || "Preview"}</Typography>
                    <IconButton size="small" edge="start" onClick={handleClosePreview} style={{ backgroundColor: "#686868", borderRadius: 2, width: 20, height: 20 }} disableRipple>
                        <CloseIcon sx={{ color: "white", fontSize: "11px" }} />
                    </IconButton>
                </Toolbar>
                {previewableFiles.length > 1 && (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 0.5, bgcolor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
                        <Button 
                            size="small"
                            variant="contained"
                            disabled={currentPreviewIndex === 0} 
                            onClick={handlePreviousPreview}
                            startIcon={<ArrowLeftTwoToneIcon />}
                            sx={{ 
                                textTransform: "none", 
                                bgcolor: "#3D5C7B", 
                                color: "white", 
                                fontSize: 12,
                                "& .MuiButton-startIcon": { marginRight: "0" }
                            }}
                        >
                            Previous
                        </Button>
                        <Typography variant="caption" fontWeight={500}>
                            {currentPreviewIndex + 1} of {previewableFiles.length} - {previewFileName}
                        </Typography>
                        <Button 
                            size="small"
                            variant="contained"
                            disabled={currentPreviewIndex >= previewableFiles.length - 1} 
                            onClick={handleNextPreview}
                            endIcon={<ArrowRightTwoToneIcon />}
                            sx={{ 
                                textTransform: "none", 
                                bgcolor: "#3D5C7B", 
                                color: "white", 
                                fontSize: 12,
                                "& .MuiButton-endIcon": { marginLeft: "0" }
                            }}
                        >
                            Next
                        </Button>
                    </Stack>
                )}
                <DialogContent sx={{ p: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: 'hidden' }}>
                    {previewUrl && (
                        <Box
                            component="iframe"
                            src={previewUrl}
                            title="File Preview"
                            sx={{
                                width: "100%",
                                height: "100%",
                                border: "none",
                                "&::-webkit-scrollbar": { width: 5, height: 5 },
                                "&::-webkit-scrollbar-thumb": { backgroundColor: "#bdbdbd", borderRadius: 10 }
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
            
            <PayrollsPopup open={alertPopup.open} popupType={alertPopup.type} message={alertPopup.message} onConfirm={alertPopup.onConfirm} onCancel={hidePopup} />
        </Stack>
    );
};

export default SharedAttachments;